import { toast } from 'sonner';

import { useAxios } from '@/hooks/useAxios';
import { getQueryKey } from '@/hooks/useQueryKeys';
import { UseExtendQueryOptions, useMutation, useQuery } from '@/utils/api';

export interface ReportItem {
  id: string;
  name: string;
  status: string;
  created_at?: number;
  client_id?: number;
  report_id?: number;
  sharedFileKey?: string;
}

export interface ReportingResponse {
  reports: ReportItem[];
  totalCount: number;
}

export interface ExportReportResponse {
  key: string;
  name: string;
  status: string;
  comment?: string;
}

export const useGetReporting = (
  options?: UseExtendQueryOptions<ReportingResponse> & {
    clientId?: string;
    customerEmail?: string;
    published?: boolean;
  }
) => {
  const axios = useAxios({ email: options?.customerEmail });

  return useQuery<ReportingResponse>({
    ...options,
    defaultErrorMessage: 'Failed to fetch reports',
    queryKey: getQueryKey.getReporting(
      options?.clientId,
      options?.customerEmail
    ),
    queryFn: async () => {
      const { data } = await axios.get('/plextrac/reporting', {
        params: options?.published
          ? {
              published: true,
            }
          : {},
      });
      return data;
    },
  });
};

export const useExportReport = (customerEmail?: string) => {
  const axios = useAxios({ email: customerEmail });

  return useMutation<ExportReportResponse, Error, { reportId: string }>({
    defaultErrorMessage: 'Failed to queue export',
    mutationFn: async ({ reportId }: { reportId: string }) => {
      const { data } = await axios.get<ExportReportResponse>(
        '/plextrac/export',
        {
          params: {
            reportId,
          },
        }
      );

      toast.success(
        'Export job queued successfully. Processing will complete shortly.'
      );

      return data;
    },
  });
};

export const useJobStatus = (
  jobKey?: string,
  options?: UseExtendQueryOptions<ExportReportResponse> & {
    customerEmail?: string;
  }
) => {
  const axios = useAxios({ email: options?.customerEmail });

  return useQuery<ExportReportResponse>({
    ...options,
    defaultErrorMessage: 'Failed to fetch job status',
    queryKey: getQueryKey.getMy('job', jobKey || ''),
    queryFn: async () => {
      if (!jobKey) {
        throw new Error('Job key is required');
      }
      const { data } = await axios.get(`/my/job`, {
        params: { key: jobKey },
      });
      return data[0];
    },
    enabled: !!jobKey && (options?.enabled ?? true),
    refetchInterval: query => {
      const job = query.state.data;
      if (job?.status === 'pending' || job?.status === 'running') {
        return 2000;
      }
      return false;
    },
  });
};

export const useUnshareReport = (customerEmail?: string) => {
  const axios = useAxios({ email: customerEmail });

  return useMutation<void, Error, { fileKey: string; reportName: string }>({
    defaultErrorMessage: 'Failed to unshare report',
    mutationFn: async ({
      fileKey,
    }: {
      fileKey: string;
      reportName: string;
    }) => {
      const promise = axios.delete('/file', {
        params: {
          name: fileKey,
        },
      });

      toast.promise(promise, {
        loading: 'Unsharing report...',
        success:
          'Report has been unshared successfully. Customers can no longer access it.',
        error: 'Failed to unshare report',
      });

      await promise;
    },
  });
};
